import {isDefined} from "../utils";
import moment from "moment";
import {BaseVueEntity} from "./BaseVueEntity";
import {appointmentsStorage} from "../main";
import {Room} from "./Room";

export class Appointment extends BaseVueEntity {

    static block_states = {
        STATE_BLOCKED_SELECTION: 1,
        STATE_BLOCKED_WAITING_FOR_RESPONSE: 2,
        STATE_BOOKED: 3,
        STATE_CANCELED: 4,
        STATE_REJECTED: 5
    }

    id;
    description;
    from;
    to;
    name;
    participants;
    requesting_user;
    custom_room;
    room_id;
    block_state;
    table_id;
    table_name;
    booth_id;
    review;
    _not_reviewed;

    constructor(appointmentAsObject) {
        super();
        for (const [key, value] of Object.entries(appointmentAsObject)) {
            this[key] = value;
        }
        if (!isDefined(this.review)) {
            this.review = {
                description: '',
                rating: 0
            }
            this._not_reviewed = true;
        }

        this.generateVueIdBasedOnProperty('id');
    }

    getRoomName() {
        if (isDefined(this.custom_room)) {
            return this.custom_room;
        }
        if (isDefined(this.room_id)) {
            const room = appointmentsStorage.getRoomById(this.room_id);
            if (isDefined(room)) {
                return room.room;
            }
        }
        return '';
    }

    getHall() {
        if (isDefined(this.room_id)) {
            const hall = appointmentsStorage.getHallByRoomId(this.room_id);
            if (isDefined(hall)) {
                return hall;
            }
        }
        if (isDefined(this.booth_id)) {
            const hall = appointmentsStorage.getHallByStandId(this.booth_id);
            if (isDefined(hall)) {
                return hall;
            }
        }
    }

    getStandName() {
        if (isDefined(this.booth_id)) {
            return appointmentsStorage.getStandName(this.booth_id);
        }
    }

    getRoom() {
        if (isDefined(this.room_id)) {
            const room = appointmentsStorage.getRoomById(this.room_id);
            if (isDefined(room)) {
                return room;
            }
        }
    }

    getTableName() {
        return this.table_name
    }

    isOnline() {
        const room = this.getRoom();
        if (isDefined(room) && room instanceof Room) {
            return room.is_online;
        }
        return false;
    }

    isConfirmed() {
        return this.block_state === Appointment.block_states.STATE_BOOKED;
    }

    isCanceled() {
        return this.block_state === Appointment.block_states.STATE_CANCELED || this.block_state === Appointment.block_states.STATE_REJECTED;
    }

    isRequest() {
        return this.block_state === Appointment.block_states.STATE_BLOCKED_WAITING_FOR_RESPONSE;
    }

    isBlocked() {
        return this.block_state === Appointment.block_states.STATE_BLOCKED_SELECTION;
    }

    isOutGoingRequest(userId) {
        return +this.requesting_user === +userId;
    }

    alreadyPast() {
        const from = moment.utc(this.from).tz(process.env.VUE_APP_GROUPS_TIMEZONE);
        const now = moment.utc().tz(process.env.VUE_APP_GROUPS_TIMEZONE);
        return !from.isAfter(now);
    }

    isDue() {
        return this.isConfirmed() && this.getMillisUntilDue() <= 0 && !this.alreadyPast();
    }

    isReadyForReview() {
        return this.isConfirmed() && this.alreadyPast();
    }

    getMillisUntilDue() {
        const millisBeforeWarning = process.env.VUE_APP_DUE_APPOINTMENTS_TIME_BEFORE_WARNING;
        const from = moment.utc(this.from).tz(process.env.VUE_APP_GROUPS_TIMEZONE).subtract(millisBeforeWarning, 'milliseconds');
        const now = moment.utc().tz(process.env.VUE_APP_GROUPS_TIMEZONE);

        return from.valueOf() - now.valueOf();
    }


    static isParticipant(appointment, userId) {
        for (const participant of appointment.participants) {
            if (+participant.user_id === +userId) {
                return true;
            }
        }
        return false;
    }

    static getCanceledAppointments(appointments) {
        return appointments.filter(a => a.block_state === Appointment.block_states.STATE_CANCELED || a.block_state === Appointment.block_states.STATE_REJECTED);
    }

    static getAppointmentRequests(appointments) {
        return appointments.filter(a => a.block_state === Appointment.block_states.STATE_BLOCKED_WAITING_FOR_RESPONSE);
    }

    static getOutgoingRequests(appointments, userId) {
        return appointments.filter(a => a.block_state === Appointment.block_states.STATE_BLOCKED_WAITING_FOR_RESPONSE && +a.requesting_user === +userId);
    }

    static getReceivedRequests(appointments, userId) {
        return appointments.filter(a => a.block_state === Appointment.block_states.STATE_BLOCKED_WAITING_FOR_RESPONSE && this.isParticipant(a, userId));
    }

    static getUnreviewedAppointments(appointment) {
        return appointment.filter(a => a.block_state === Appointment.block_states.STATE_BOOKED && a._not_reviewed && a.alreadyPast());
    }

    static getConfirmedRequests(appointments) {
        return appointments.filter(a => a.block_state === Appointment.block_states.STATE_BOOKED);
    }

    static getBlockedAppointments(appointments) {
        return appointments.filter(a => a.block_state === Appointment.block_states.STATE_BLOCKED_SELECTION);
    }


}
